import { useState, useEffect, useCallback } from 'react'
import { axiosInstanceMain } from '../utils/axiosGlobalUtils'
import AdminAbmConfigurations from '../components/AdminAbmConfig/AdminAbmConfigurations'
import ButtonCache from '../components/Common/ButtonCache'
import Buscador from '../components/Common/Buscador'
import { CircularProgress, useTheme, useMediaQuery, styled, TextField, Button } from '@mui/material'
import Navbar from '../components/Navbar'
import Footer from './Home/components/Footer'
import { Stack } from '@mui/system'
import SnackBarUtils from '../utils/SnackBarUtils'
import CopyCodeId from './ModalCopyCodeId'

const Title = styled('span')(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  [theme.breakpoints.down(425)]: {
    fontSize: '1.2rem',
    textAlign: 'center'
  }
}))

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'row',
    '& > *': {
      margin: '10px 0px 0px 0px'
    }
  }
}))

const ConfiguracionesContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  margin: '20px 0px'
})

export interface IConfig {
  name: string
  value: string
  isEditing: boolean
  newValue: string
}

const AdminAbmConfigPage = () => {
  const [configs, setConfigs] = useState<IConfig[]>([])
  const [configsSearched, setConfigsSearched] = useState<IConfig[]>([])
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [loadingData, setLoadingData] = useState<boolean>(false)
  const [data, setData] = useState<string>(null)
  const [result, setResult] = useState<string>()
  const [openCopyClickboard, setOpenClickboard] = useState<boolean>(false)
  const [identifier, setIdentifier] = useState<'código' | 'ID'>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    getConfigs()
  }, [])

  const getConfigs = async () => {
    await axiosInstanceMain
      .get('/admin/config')
      .then((result) => {
        setConfigs([...result.data.data, { ...result.data.data, isEditing: false }])
        setLoading(false)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const encodeId = useCallback(async (id: string) => {
    try {
      setLoadingData(true)
      await axiosInstanceMain.post('admin/encode-id', id).then((res) => {
        setResult(res.data.data)
        setOpenClickboard(true)
        setIdentifier('código')
      })
    } catch (e) {
      console.log(e)
      SnackBarUtils.error('Error codificando el id')
    } finally {
      setLoadingData(false)
    }
  }, [])

  const decodeCode = useCallback(async (code: string) => {
    try {
      setLoadingData(true)
      await axiosInstanceMain
        .post('admin/decode-cod', code, {
          headers: {
            'Content-Type': 'text/plain'
          }
        })
        .then((res) => {
          setResult(res.data.data)
          setOpenClickboard(true)
          setIdentifier('ID')
        })
    } catch (e) {
      console.log(e)
      SnackBarUtils.error('Error decodificando el código')
    } finally {
      setLoadingData(false)
    }
  }, [])

  return (
    <>
      <Navbar title="Admin ABM Configuración" />

      <div className="p-4">
        <Header>
          {/* <AdminAbmConfigSearch configs={configs} setConfigsSearched={setConfigsSearched} /> */}
          <Buscador
            elements={configs}
            setElementsSearched={setConfigsSearched}
            searchBy={['name', 'value']}
            setIsSearching={setIsSearching}
          />
          {!isMobile && <ButtonCache />}
          <Stack
            direction={isMobile ? 'column' : 'row'}
            gap={'10px'}
            sx={{ marginBottom: '20px', marginTop: '10px', ...(isMobile && { width: '50%' }) }}
          >
            <TextField
              disabled={loadingData}
              label="ID / Código"
              variant="outlined"
              value={data}
              onChange={(e) => setData(e.target.value)}
            />
            <Stack direction={'row'} gap={'10px'}>
              <Button
                disabled={loadingData}
                variant="contained"
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                onClick={() => encodeId(data)}
              >
                Encodear
              </Button>
              <Button
                disabled={loadingData}
                variant="contained"
                color="secondary"
                size={isMobile ? 'small' : 'medium'}
                onClick={() => decodeCode(data)}
              >
                Decodear
              </Button>
            </Stack>
          </Stack>
          {isMobile && <ButtonCache />}
        </Header>

        {/* Configuraciones */}
        <ConfiguracionesContainer>
          <Title>Lista de Configuraciones</Title>
          {loading ? (
            <CircularProgress />
          ) : (
            <AdminAbmConfigurations
              isSearching={isSearching}
              configs={configs}
              setConfigs={setConfigs}
              configsSearched={configsSearched}
            />
          )}
        </ConfiguracionesContainer>
      </div>
      <CopyCodeId open={openCopyClickboard} setOpen={setOpenClickboard} identifier={identifier} data={result} />
      {!loading && <Footer />}
    </>
  )
}

export default AdminAbmConfigPage
